import { useState } from "react";
import { MapPin, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AddressAutocomplete from "./AddressAutocomplete";
import { errorMessage } from "@/lib/errors";

/**
 * WaypointPanel — passenger UI to add intermediate stops to an active ride.
 *
 * Shows the current stops list, an "Add a stop" toggle that reveals an
 * AddressAutocomplete input, and a pending-confirmation pill when the
 * driver hasn't yet one-tap-confirmed the new fare.
 *
 * Props:
 *  • ride: the active ride doc (has waypoints[], waypoint_pending_total)
 *  • onUpdated(ride): callback after add/remove succeeds (parent refresh)
 */
export default function WaypointPanel({ ride, onUpdated }) {
  const [adding, setAdding] = useState(false);
  const [stop, setStop] = useState({ lat: 0, lng: 0, address: "" });
  const [busy, setBusy] = useState(false);
  const stops = ride?.waypoints || [];
  const pendingTotal = ride?.waypoint_pending_total;
  const isOver = ride?.status === "completed" || ride?.status === "cancelled";
  const max = 3;

  const submit = async () => {
    if (!stop.address || !stop.lat || !stop.lng) {
      toast.error("Pick an address for the stop");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/chauffeur/rides/${ride.id}/waypoint`, {
        lat: stop.lat,
        lng: stop.lng,
        address: stop.address,
      });
      toast.success(`Stop added — new total JMD ${res.data.estimated_fare}`);
      setStop({ lat: 0, lng: 0, address: "" });
      setAdding(false);
      onUpdated?.(res.data);
    } catch (err) {
      toast.error(errorMessage(err, "Could not add stop"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (wpId) => {
    setBusy(true);
    try {
      const res = await api.delete(`/chauffeur/rides/${ride.id}/waypoint/${wpId}`);
      toast.success("Stop removed");
      onUpdated?.(res.data);
    } catch (err) {
      toast.error(errorMessage(err, "Could not remove stop"));
    } finally {
      setBusy(false);
    }
  };

  if (isOver) return null;

  return (
    <div data-testid="waypoint-panel" className="p-4 border border-gold/20 bg-white/5 rounded-md space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin size={14} className="text-gold" />
          <p className="text-xs font-body uppercase tracking-wider text-gold">Stops</p>
        </div>
        <span className="text-[10px] text-white/45">{stops.length}/{max}</span>
      </div>

      {stops.length === 0 && !adding && (
        <p className="text-[11px] text-white/45">Need to swing by somewhere on the way? Add up to {max} stops — fare auto-adjusts.</p>
      )}

      {stops.length > 0 && (
        <ol className="space-y-1.5">
          {stops.map((w, idx) => (
            <li key={w.id || idx} data-testid={`waypoint-row-${idx}`}
              className="flex items-center gap-2 text-xs text-white/80 bg-black/30 border border-white/10 rounded px-2 py-1.5">
              <span className="text-gold font-display">{idx + 1}</span>
              <span className="flex-1 truncate">{w.address}</span>
              <button
                data-testid={`waypoint-remove-${idx}`}
                onClick={() => remove(w.id)}
                disabled={busy}
                aria-label="Remove stop"
                className="text-rose-300/70 hover:text-rose-300 disabled:opacity-30"
              ><X size={14} /></button>
            </li>
          ))}
        </ol>
      )}

      {pendingTotal && (
        <div data-testid="waypoint-pending-pill" className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-400/40 text-[11px] text-amber-200">
          <Loader2 size={12} className="animate-spin" />
          Waiting for driver to confirm JMD {pendingTotal}…
        </div>
      )}

      {adding ? (
        <div className="space-y-2">
          <AddressAutocomplete
            data-testid="waypoint-autocomplete"
            label="Stop address"
            placeholder="Where do you need to swing by?"
            value={stop}
            onChange={(next) => setStop({ ...stop, ...next })}
            nearLat={ride?.pickup?.lat || null}
            nearLng={ride?.pickup?.lng || null}
            kind="waypoint"
          />
          <div className="flex items-center gap-2">
            <button data-testid="waypoint-add-confirm" onClick={submit} disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-body uppercase tracking-wider bg-gold text-luxury-black hover:bg-gold/90 disabled:opacity-50 rounded">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add stop
            </button>
            <button data-testid="waypoint-add-cancel" onClick={() => { setAdding(false); setStop({ lat: 0, lng: 0, address: "" }); }}
              className="px-3 py-2 text-xs text-white/60 hover:text-white border border-white/15 rounded">Cancel</button>
          </div>
          <p className="text-[10px] text-white/40">JMD 250 surcharge per stop + distance recalc via OSRM.</p>
        </div>
      ) : (
        stops.length < max && (
          <button data-testid="waypoint-add-btn" onClick={() => setAdding(true)} disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-body uppercase tracking-wider text-gold border border-gold/30 hover:bg-gold/10 disabled:opacity-50 rounded">
            <Plus size={12} /> Add a stop
          </button>
        )
      )}
    </div>
  );
}
